/**
 * @file MillEmailsManager.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-09-30
 * @description Transport Settings → Mill Emails: where each mill's order
 *              summary goes when transport planning sends it — as many
 *              addresses per mill as it wants.
 *
 *              A mill not listed gets its contact record's email, as it always
 *              has. The send modals show these addresses read-only, so this is
 *              the one place they change. Server: services/paperworkRecipientsService.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './MillEmailsManager.css';
import Input from './Input';
import Modal from './Modal';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import SearchHelp from './SearchHelp';
import { useSlideOutButton } from './SlideOutButton';
import { matchesAllTerms } from '../utils/searchMatch';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';

const CLEARED_FILTERS = { search: '' };

function AddMillBody({ suppliers, existingIds, onAdd, onClose }) {
  const [accountId, setAccountId] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  useSlideOutButton([saving]);
  const options = useMemo(() => [
    { value: '', label: 'Choose a mill...' },
    ...suppliers.filter(s => !existingIds.has(s.id)).map(s => ({
      value: String(s.id), label: s.company_name, badge: s.account_no || undefined,
      searchText: `${s.company_name} ${s.account_no || ''} ${s.email || ''}`
    }))
  ], [suppliers, existingIds]);
  const chosen = suppliers.find(s => String(s.id) === accountId);
  const save = async () => {
    setSaving(true);
    const ok = await onAdd(parseInt(accountId, 10), email.trim());
    setSaving(false);
    if (ok) onClose();
  };
  return (
    <>
      <div className="mem-field">
        <label>Mill</label>
        <SearchableDropdown value={accountId} onChange={(v) => setAccountId(v || '')} options={options}
          placeholder="Choose a mill..." searchPlaceholder="Search mills..." />
        {chosen?.email && <span className="mem-hint">Contact record email: {chosen.email}</span>}
      </div>
      <div className="mem-field">
        <label>Paperwork email</label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="orders@mill.co.uk" />
        <span className="mem-hint">Add more addresses from the list once the mill is added.</span>
      </div>
      <div className="mem-actions">
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={save} disabled={!accountId || !email.trim() || saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving...' : 'Add mill'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function MillEmailsManager() {
  const [rows, setRows] = useState([]);
  const [ready, setReady] = useState(true);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState(CLEARED_FILTERS);
  const [drafts, setDrafts] = useState({});     // accountId → address being typed
  const [adding, setAdding] = useState(false);
  const [alert, setAlert] = useState({ isOpen: false, message: '', type: 'info' });
  useSlideOutButton([rows.length]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/supplier-paperwork-emails`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load mill emails');
      setRows(data.suppliers || []);
      setReady(data.ready !== false);
    } catch (e) {
      setAlert({ isOpen: true, message: e.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=SUPPLIER&limit=10000`, { credentials: 'include' })
      .then(r => r.json()).then(d => setSuppliers(d.contacts || [])).catch(() => {});
  }, [load]);

  const add = async (accountId, email) => {
    try {
      const res = await fetch(`${API_URL}/api/transport/supplier-paperwork-emails`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: accountId, email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add the address');
      setDrafts(d => ({ ...d, [accountId]: '' }));
      await load();
      return true;
    } catch (e) {
      setAlert({ isOpen: true, message: e.message, type: 'error' });
      return false;
    }
  };

  const remove = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/transport/supplier-paperwork-emails/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove the address');
      await load();
    } catch (e) {
      setAlert({ isOpen: true, message: e.message, type: 'error' });
    }
  };

  const visible = rows.filter(r => matchesAllTerms(
    [r.company_name, r.account_no, r.account_email, ...(r.emails || []).map(e => e.email)], filters.search));
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS);
  const filtersApplied = headerFilters.count > 0;

  return (
    <div className="search-layout mill-emails-manager">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Mill Emails</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>Where each mill's order summary goes from transport planning. Mills not listed get their contact record's email.</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button className={clearFiltersClass(filtersApplied)} onClick={() => setFilters(CLEARED_FILTERS)}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => setAdding(true)} disabled={!ready} data-write-action>
                <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                <span className="btn-label">Add Mill</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row">
          <div className="filter-group">
            <label>Search <SearchHelp fields={['mill', 'account no.', 'email']} /></label>
            <Input type="text" icon="search" placeholder="Search mills or addresses..."
              value={filters.search} onChange={(e) => setFilters({ search: e.target.value })} />
          </div>
        </div>
      </form>

      <div className="search-content">
        {!ready && (
          <p className="mem-notice">Mill emails are not available yet — the database migration has not been applied. Paperwork still goes to each mill's contact email.</p>
        )}
        <div className="table-container">
          {loading ? <LoadingSpinner /> : (
            <table className="search-results-table mem-table">
              <thead>
                <tr><th>Mill</th><th>Paperwork addresses</th></tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr><td colSpan="2" className="no-results">{rows.length ? 'No mills match' : 'No mills set yet — every mill gets its contact record\'s email.'}</td></tr>
                ) : visible.map(r => (
                  <tr key={r.account_id}>
                    <td className="mem-mill">
                      <strong>{r.company_name}</strong>
                      {r.account_no && <span className="account-badge-inline">{r.account_no}</span>}
                    </td>
                    <td>
                      <div className="mem-chips">
                        {(r.emails || []).map(e => (
                          <span key={e.id} className="mem-chip">
                            {e.email}
                            <button type="button" title={`Remove ${e.email}`} data-write-action onClick={() => remove(e.id)}>
                              <span className="material-symbols-rounded">close</span>
                            </button>
                          </span>
                        ))}
                        <form className="mem-add" onSubmit={(ev) => { ev.preventDefault(); if ((drafts[r.account_id] || '').trim()) add(r.account_id, drafts[r.account_id].trim()); }}>
                          <input className="styled-input" type="email" placeholder="Add address..."
                            value={drafts[r.account_id] || ''} onChange={(ev) => setDrafts(d => ({ ...d, [r.account_id]: ev.target.value }))} />
                          <button type="submit" className="btn-icon" title="Add address" data-write-action
                            disabled={!(drafts[r.account_id] || '').trim()}>
                            <span className="material-symbols-rounded">add</span>
                          </button>
                        </form>
                      </div>
                      {r.emails?.length === 1 && (
                        <span className="mem-hint">Removing the last address returns this mill to its contact record's email{r.account_email ? ` (${r.account_email})` : ''}.</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal isOpen={adding} onClose={() => setAdding(false)} title="Add Mill" size="sm" className="mill-emails-add">
        {adding && (
          <AddMillBody suppliers={suppliers} existingIds={new Set(rows.map(r => r.account_id))}
            onAdd={add} onClose={() => setAdding(false)} />
        )}
      </Modal>

      <AlertDialog isOpen={alert.isOpen} message={alert.message} type={alert.type}
        onClose={() => setAlert(a => ({ ...a, isOpen: false }))} />
    </div>
  );
}

export default MillEmailsManager;
